import { ArrowLeft, Pencil, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsNote, useNote } from '@/api/hooks';
import { LIBELLE_NATURE_CHANGEMENT, type DecisionApi } from '@/api/types';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, ControlesListe, DecisionDialog, ErreurBloc, EtapeBadge, Historique, PageHeader, StatutBadge } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateHeureFr, montant } from '@/lib/utils';

const NATURE_COUT: Record<string, string> = { PERDIEM: 'Perdiem', LOGEMENT: 'Logement', HEBERGEMENT: 'Hébergement', TRANSPORT: 'Transport', PROTOCOLE: 'Protocole', REPRESENTATION: 'Représentation', FRAIS_TERMINAUX: 'Frais terminaux' };

export function NoteDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useNote(id);
  const actions = useActionsNote();
  const [dialogue, setDialogue] = useState<'decision' | 'soumettre' | 'resoumettre' | null>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { note, circuit, actions: a } = data;
  const erreur = (e: unknown) => {
    const c = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
  };
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const decider = (decision: DecisionApi, motif?: string) =>
    executer(() => actions.decider.mutateAsync({ id: note.id, decision, motif }), decision === 'VALIDER' && a.etape === 'DIRECTEUR_GENERAL' ? (note.impactBudgetaire ? 'Note autorisée : montants du TDR révisés, FED libérés.' : 'Note autorisée.') : 'Décision enregistrée.');

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to={`/tdr/${note.tdrId}`} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> TDR {data.tdr.numero}
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {note.numero ?? 'Note explicative (brouillon)'} <StatutBadge statut={note.statut} /> {note.statut === 'SOUMIS' && <EtapeBadge etape={data.etapeCourante} />}
            <Badge variant={note.impactBudgetaire ? 'destructive' : 'outline'}>{note.impactBudgetaire ? 'Incidence budgétaire' : 'Sans incidence budgétaire'}</Badge>
          </span>
        }
        sousTitre={
          <>
            {note.objet} · {note.structure?.libelle} · émise par {note.emetteur?.prenom} {note.emetteur?.nom}
            {note.dateSoumission ? ` · soumise le ${dateHeureFr(note.dateSoumission)}` : ''}
            {note.dateAutorisation ? ` · autorisée le ${dateHeureFr(note.dateAutorisation)}` : ''}
          </>
        }
        actions={
          <>
            <BoutonPdf chemin={`/pdf/notes/${note.id}`} nom={`${note.numero ?? 'NE'}.pdf`} />
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/ne/${note.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Budget du TDR avant</div>
              <div className="num text-xl font-semibold">{montant(note.montantAncien, true)}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Budget révisé</div>
              <div className="num text-xl font-semibold">{montant(note.montantNouveau, true)}</div>
            </div>
            <div className={cn('rounded-lg border p-4', data.ecart > 0 ? 'bg-amber-50' : data.ecart < 0 ? 'bg-emerald-50' : 'bg-card')}>
              <div className="text-xs uppercase text-muted-foreground">Écart</div>
              <div className="num text-xl font-semibold">
                {data.ecart >= 0 ? '+' : ''}
                {montant(data.ecart, true)}
              </div>
            </div>
          </div>

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesListe controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Changements et motifs</CardTitle>
              <CardDescription className="flex flex-wrap gap-1.5 pt-1">
                {note.natures.map((n) => (
                  <Badge key={n} variant="secondary">
                    {LIBELLE_NATURE_CHANGEMENT[n]}
                  </Badge>
                ))}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line text-sm">{note.motifs}</p>
            </CardContent>
          </Card>

          {note.impactBudgetaire && note.budget && (
            <Card>
              <CardHeader>
                <CardTitle>Budget révisé par nature</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bloc</TableHead>
                      <TableHead>Nature</TableHead>
                      <TableHead className="text-right">Ancien</TableHead>
                      <TableHead className="text-right">Nouveau</TableHead>
                      <TableHead className="text-right">Écart</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {note.budget.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>{l.bloc}</TableCell>
                        <TableCell>{NATURE_COUT[l.nature] ?? l.nature}</TableCell>
                        <TableCell className="num text-right">{montant(l.ancien, true)}</TableCell>
                        <TableCell className="num text-right">{montant(l.nouveau, true)}</TableCell>
                        <TableCell className={cn('num text-right', l.nouveau > l.ancien && 'text-amber-700', l.nouveau < l.ancien && 'text-emerald-700')}>
                          {l.nouveau - l.ancien >= 0 ? '+' : ''}
                          {montant(l.nouveau - l.ancien, true)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          <PiecesJointes objetType="NE" objetId={note.id} typesSuggeres={['JUSTIFICATIF', 'COURRIER', 'AUTRE']} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {circuit && <CardDescription>Circuit complet · {circuit.steps.length} étapes</CardDescription>}
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={circuit} />
            </CardContent>
          </Card>
          {data.feds.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>FED du TDR</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {data.feds.map((f) => (
                  <div key={f.id} className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/fed/${f.id}`} className="font-medium text-primary hover:underline">
                      {f.numero ?? 'FED (brouillon)'}
                    </Link>
                    <span className="flex items-center gap-1">
                      <StatutBadge statut={f.statut} />
                      {f.suspendu && <Badge variant="destructive">suspendu</Badge>}
                    </span>
                    <span className="num">{montant(f.montantUsd, true)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog
        ouvert={dialogue === 'soumettre'}
        onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)}
        titre="Soumettre la note"
        description={note.impactBudgetaire ? 'La note suit le circuit complet ; les FED du TDR sont suspendus jusqu’à la décision finale.' : 'La note suit le circuit complet de validation.'}
        libelle="Soumettre"
        onConfirmer={() => executer(() => actions.soumettre.mutateAsync(note.id), 'Note soumise.')}
        chargement={actions.soumettre.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'resoumettre'}
        onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)}
        titre="Soumettre à nouveau"
        libelle="Soumettre à nouveau"
        onConfirmer={(r) => executer(() => actions.resoumettre.mutateAsync({ id: note.id, reponse: r || undefined }), 'Note soumise à nouveau.')}
        chargement={actions.resoumettre.isPending}
        champ={{ label: 'Réponse au validateur' }}
      />
    </div>
  );
}
